import Button from "@/components/Button"
import { CopyTextButton } from "@/components/CopyText"
import DevErrorToast from "@/components/DevErrorToast"
import Dialog from "@/components/Dialog"
import InputErrorMessage from "@/components/InputErrorMessage"
import {
  CheckboxInput,
  Combobox,
  DateInput,
  EmailInput,
  PhoneInput,
  SelectInput,
  TextInput,
} from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import { useEditPatientInformationMutation } from "@/data/queries"
import { UpdatePatientDto } from "@/data/requests"
import { Organization } from "@/model/Organization"
import { Patient } from "@/model/Patient"
import { usTimeZoneOptions } from "@/utils/timezoneOptions"
import { zodResolver } from "@hookform/resolvers/zod"
import { LocalDate } from "@js-joda/core"
import { isEqual } from "lodash"
import { ChangeEvent, useMemo } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { formatPhoneNumber } from "../utils"
import { formSchema } from "./formSchema"

type EditPatientDialogProps = {
  patient: Patient
  patientOrganization: Organization
  isOpen: boolean
  onClose: () => void
}

type EditPatientDialogFormData = {
  firstName: string
  lastName: string
  emailAddress?: string
  phoneNumber: string
  dateOfBirth?: string
  preferredLocale?: string
  timeZone: string
  shippingAddress?: {
    address1?: string
    address2?: string
    city?: string
    state?: string
    zipCode?: string
  }
  insuranceProvider?: string
  isAttCompatible?: boolean
}

const EditPatientDialog = ({
  patient,
  patientOrganization,
  isOpen,
  onClose,
}: EditPatientDialogProps) => {
  const supportedLocales = patientOrganization.supportedLocales ?? []
  const localesOptions = supportedLocales.map((e) => ({
    value: e.locale,
    title: e.name,
  }))
  const preferredLocale = supportedLocales.find(
    (e) => e.locale === patient.preferredLocale
  )?.locale

  const initialFormValues: EditPatientDialogFormData = Object.freeze({
    firstName: patient.firstName ?? "",
    lastName: patient.lastName ?? "",
    emailAddress: patient.emailAddress ?? "",
    phoneNumber: formatPhoneNumber(patient.phoneNumber ?? ""),
    dateOfBirth: patient.dateOfBirth?.toString(),
    preferredLocale: preferredLocale,
    shippingAddress: patient.shippingAddress,
    insuranceProvider: patient.insuranceProvider,
    timeZone: patient.timeZone,
    isAttCompatible: patient.isAttCompatible,
  })

  const timeZoneOptions = useMemo(() => usTimeZoneOptions, [])

  const { register, setValue, handleSubmit, formState, watch, reset } =
    useForm<EditPatientDialogFormData>({
      mode: "all",
      defaultValues: initialFormValues,
      resolver: zodResolver(formSchema),
    })

  const { errors } = formState
  const formValues = watch()
  const formHasChanges = !isEqual(formValues, initialFormValues)

  const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setValue("phoneNumber", formatted, { shouldValidate: true })
  }

  const onSelectTimeZone = (value: string | null) => {
    setValue("timeZone", value ?? "", { shouldValidate: true })
  }

  const { mutateAsync: editPatientInfo, isPending: isEditingPatientInfo } =
    useEditPatientInformationMutation(patient.bloomlifeId)

  const canSubmit = formHasChanges && !isEditingPatientInfo

  const onSubmit = async (formData: EditPatientDialogFormData) => {
    const payload = toUpdatePatientDto(formData)
    if (patient.emailAddress === payload.emailAddress) {
      delete payload.emailAddress
    }
    try {
      await editPatientInfo({
        bloomlifeId: patient.bloomlifeId,
        update: payload,
      })
      onClose()
      toast.custom((toastId) => (
        <Toast
          toastId={toastId}
          description="Patient information updated successfully."
        />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while editing this patient information. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  return (
    <Dialog
      title="Edit patient info"
      isOpen={isOpen}
      onClose={() => {
        reset()
        onClose()
      }}
    >
      <div>
        <form
          className="mt-6"
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="mrn" className="ml-1">
                MRN
              </InputLabel>
              <div className="relative w-full">
                <TextInput
                  id="mrn"
                  disabled
                  placeholder="MRN"
                  value={patient.medicalRecordNumber}
                />
                <CopyTextButton
                  text={patient.medicalRecordNumber}
                  label="MRN"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="mrn" className="ml-1">
                Bloomlife ID
              </InputLabel>
              <div className="relative w-full">
                <TextInput
                  id="bloomlife-id"
                  disabled
                  placeholder="Bloomlife ID"
                  value={patient.bloomlifeId}
                />
                <CopyTextButton
                  text={patient.bloomlifeId}
                  label="Bloomlife ID"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="first-name" className="ml-1">
                First name
              </InputLabel>
              <TextInput
                id="first-name"
                placeholder="First name"
                isError={!!errors?.firstName?.message}
                {...register("firstName")}
              />
              <InputErrorMessage
                message={errors?.firstName?.message}
                className="ml-1"
              />
            </div>
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="last-name" className="ml-1">
                Last name
              </InputLabel>
              <TextInput
                id="last-name"
                placeholder="Last name"
                isError={!!errors?.lastName?.message}
                {...register("lastName")}
              />
              <InputErrorMessage
                message={errors?.lastName?.message}
                className="ml-1"
              />
            </div>
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="date-of-birth" className="ml-1">
                Date of birth
              </InputLabel>
              <DateInput
                id="date-of-birth"
                isError={!!errors?.dateOfBirth?.message}
                max={LocalDate.now().toString()}
                {...register("dateOfBirth")}
              />
              <InputErrorMessage
                message={errors?.dateOfBirth?.message}
                className="ml-1"
              />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="email" className="ml-1">
                Email address
              </InputLabel>
              <EmailInput
                id="email"
                placeholder="Email address"
                isError={!!errors?.emailAddress?.message}
                disabled={
                  !!patient.emailAddress && patient.loginState === "active"
                }
                {...register("emailAddress")}
              />
              <InputErrorMessage
                message={errors?.emailAddress?.message}
                className="ml-1"
              />
            </div>

            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="phone-number" className="ml-1">
                Phone number
              </InputLabel>
              <PhoneInput
                id="phone-number"
                placeholder="Phone number"
                isError={!!errors?.phoneNumber?.message}
                {...register("phoneNumber")}
                onChange={handlePhoneNumberChange}
              />
              <InputErrorMessage
                message={errors?.phoneNumber?.message}
                className="ml-1"
              />
            </div>
          </div>
          <div className="my-4 grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="time-zone" className="ml-1">
                Time zone
              </InputLabel>
              <Combobox
                id="time-zone"
                options={timeZoneOptions}
                value={formValues.timeZone}
                onSelect={onSelectTimeZone}
                placeholder="Search timezone..."
                {...register("timeZone")}
              />
              <InputErrorMessage
                message={errors?.timeZone?.message}
                className="ml-1"
              />
            </div>
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="language" className="ml-1">
                Preferred language
              </InputLabel>
              <SelectInput
                id="language"
                options={localesOptions}
                {...register("preferredLocale")}
              />
              <InputErrorMessage
                message={errors?.preferredLocale?.message}
                className="ml-1"
              />
            </div>
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="insurance-provider" className="ml-1">
                Insurance provider
              </InputLabel>
              <TextInput
                id="insurance-provider"
                placeholder="Insurance provider"
                isError={!!errors?.insuranceProvider?.message}
                {...register("insuranceProvider")}
              />
              <InputErrorMessage
                message={errors?.insuranceProvider?.message}
                className="ml-1"
              />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="shipping-address" className="ml-1">
                Shipping address
              </InputLabel>
              <TextInput
                id="shipping-address"
                placeholder="Shipping address"
                isError={!!errors?.shippingAddress?.address1?.message}
                {...register("shippingAddress.address1")}
              />
              <InputErrorMessage
                message={errors?.shippingAddress?.message}
                className="ml-1"
              />
            </div>
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="shipping-address-2" className="ml-1">
                Address 2
              </InputLabel>
              <TextInput
                id="shipping-address-2"
                placeholder="Address 2"
                isError={!!errors?.shippingAddress?.address2?.message}
                {...register("shippingAddress.address2")}
              />
              <InputErrorMessage
                message={errors?.shippingAddress?.address2?.message}
                className="ml-1"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="shipping-city" className="ml-1">
                City
              </InputLabel>
              <TextInput
                id="shipping-city"
                placeholder="City"
                isError={!!errors?.shippingAddress?.city?.message}
                {...register("shippingAddress.city")}
              />
              <InputErrorMessage
                message={errors?.shippingAddress?.city?.message}
                className="ml-1"
              />
            </div>

            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="shipping-state" className="ml-1">
                State
              </InputLabel>
              <TextInput
                id="shipping-state"
                placeholder="State"
                isError={!!errors?.shippingAddress?.state?.message}
                {...register("shippingAddress.state")}
              />
              <InputErrorMessage
                message={errors?.shippingAddress?.state?.message}
                className="ml-1"
              />
            </div>

            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="shipping-zip" className="ml-1">
                Zip
              </InputLabel>
              <TextInput
                id="shipping-zip"
                placeholder="Zip"
                isError={!!errors?.shippingAddress?.zipCode?.message}
                {...register("shippingAddress.zipCode")}
              />
              <InputErrorMessage
                message={errors?.shippingAddress?.zipCode?.message}
                className="ml-1"
              />
            </div>

            <div className="flex flex-row gap-1 items-center">
              <CheckboxInput
                id="att-compatibility"
                {...register("isAttCompatible")}
              />
              <InputLabel htmlFor="att-compatibility" className="ml-1">
                AT&T Compatibility
              </InputLabel>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <div className="flex gap-3">
              <Button
                type="submit"
                variant="primary"
                className="flex items-center gap-3 px-6"
                disabled={!canSubmit}
              >
                {isEditingPatientInfo && <Spinner className="size-4" />}
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Dialog>
  )
}

const toUpdatePatientDto = (
  formData: EditPatientDialogFormData
): UpdatePatientDto => ({
  firstName: formData.firstName,
  lastName: formData.lastName,
  phoneNumber: `+1${formData.phoneNumber.replace(/\D/g, "")}`, // Send +1 version to backend
  emailAddress: formData.emailAddress || undefined,
  dateOfBirth: formData.dateOfBirth || undefined,
  preferredLocale: formData.preferredLocale || undefined,
  timeZone: formData.timeZone || undefined,
  insuranceProvider: formData.insuranceProvider || undefined,
  shippingAddress: formData.shippingAddress
    ? {
      address1: formData.shippingAddress.address1 || undefined,
      address2: formData.shippingAddress.address2 || undefined,
      city: formData.shippingAddress.city || undefined,
      state: formData.shippingAddress.state || undefined,
      zipCode: formData.shippingAddress.zipCode || undefined,
    }
    : undefined,
  isAttCompatible: formData.isAttCompatible === undefined ? undefined : formData.isAttCompatible,
})

export default EditPatientDialog
